import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import cond from 'lodash/cond';
import stubTrue from 'lodash/stubTrue';
import { useMutation } from 'react-query';
import { SuiSpinner } from '@splashthat/sui';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useLocalStorage } from '@rehooks/local-storage';

import Logger from 'observability/Logger';
import VirtualSettingsAttendeeApiClient from 'lib/SplashApiClient/subclients/VirtualSettingsAttendeeApiClient';
import getSplashApiClientV2 from 'lib/SplashApiClient/singletonV2';
import { useTouchpointData } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';
import EmbedPlaceholder from 'common/compounds/VirtualEventRenderer/states/DuringEvent/components/nodes/Embed/components/EmbedPlaceholder';

import GoldcastEmbed from './GoldcastEmbed';
import GoldcastMessage from './GoldcastMessage';

const LOG = new Logger('GoldcastEmbedContainer');

const SpinnerWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
`;

// Creation endpoint uses different field names than the embed config shape.
const toEmbedConfig = (response) => ({
    eventId: response?.goldcastEvent?.id,
    attendee: {
        shortId: response?.shortId,
        secretCode: response?.secretCode,
        emailId: response?.email?.id,
    },
});

const GoldcastEmbedContainer = ({ config }) => {
    const { eventId: goldcastEventId, attendee, attendeeCreationRequired } = config ?? {};

    const { event } = useTouchpointData();
    const [vepTokens] = useLocalStorage(`splashVirtualEvent/${event?.id}`);
    const guestAccessHash = vepTokens?.guestAccessHash;

    const hasAttendeeConfig = Boolean(goldcastEventId && attendee);
    const canCreateAttendee = Boolean(goldcastEventId && event?.id && guestAccessHash);

    const {
        mutate: createAttendee,
        status,
        data: createdConfig,
    } = useMutation({
        mutationFn: async () => {
            const attendeeApiClient = new VirtualSettingsAttendeeApiClient(getSplashApiClientV2(), guestAccessHash);
            const response = await attendeeApiClient.createGoldcastAttendee(event?.id, goldcastEventId);
            const newConfig = toEmbedConfig(response);
            const { shortId, secretCode, emailId } = newConfig.attendee ?? {};
            if (!newConfig.eventId || !shortId || !secretCode || !emailId) {
                throw new Error('Incomplete attendee config returned from creation');
            }
            return newConfig;
        },
        onError: (error) => {
            LOG.error('Attendee creation failed', error);
        },
    });

    React.useEffect(() => {
        if (!hasAttendeeConfig && !attendeeCreationRequired) {
            LOG.error('Attendee config missing and creation not required: possible misconfiguration', {
                goldcastEventId,
            });
        }
    }, [hasAttendeeConfig, attendeeCreationRequired, goldcastEventId]);

    // status !== 'idle' prevents re-creation across re-renders once mutate() has been called.
    React.useEffect(() => {
        if (!attendeeCreationRequired || status !== 'idle') {
            return;
        }
        if (!canCreateAttendee) {
            LOG.error('Attendee creation skipped: missing required params', {
                goldcastEventId,
                eventId: event?.id,
                guestAccessHash,
            });
            return;
        }
        createAttendee();
    }, [attendeeCreationRequired, canCreateAttendee, status, createAttendee]);

    return cond([
        [() => hasAttendeeConfig, () => <GoldcastEmbed config={config} />],
        [
            () => !hasAttendeeConfig && !(attendeeCreationRequired && canCreateAttendee),
            () => <GoldcastMessage message="Something went wrong. Please try again later." />,
        ],
        [() => status === 'error', () => <EmbedPlaceholder onClick={() => window.location.reload()} editing={false} />],
        [() => status === 'success', () => <GoldcastEmbed config={createdConfig} />],
        [
            stubTrue,
            () => (
                <SpinnerWrapper>
                    <SuiSpinner loading size="large" />
                    <VisuallyHidden role="status">loading</VisuallyHidden>
                </SpinnerWrapper>
            ),
        ],
    ])();
};

GoldcastEmbedContainer.propTypes = {
    config: PropTypes.shape({
        eventId: PropTypes.string,
        attendee: PropTypes.shape({
            shortId: PropTypes.string,
            secretCode: PropTypes.string,
            emailId: PropTypes.string,
        }),
        attendeeCreationRequired: PropTypes.bool,
    }),
};

GoldcastEmbedContainer.defaultProps = {
    config: null,
};

export default GoldcastEmbedContainer;
